import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Tab,
  Tabs,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TenantAutocomplete from "./TenantAutocomplete";
import { Monitor, StopCircle, Trash2, Info, Eye, Download } from "lucide-react";
import { Helmet } from "react-helmet-async";
import SyntaxHighlighter from "react-syntax-highlighter";
import { vs2015 } from "react-syntax-highlighter/dist/esm/styles/hljs";
import axiosInstance from "../axiosInstance";
import axiosInstanceImporter from "../axiosInstanceImporter";
import axiosInstanceWebScraper from "../axiosInstanceWebScraper";
import { isAdminUser } from "../config/adminUsers";
import { getUserEmail } from "../utils/getUserEmail";
import { OnboardingProcess } from "./OnboardTenants/components/OnboardingProcess";
import { useOnboardingProcess } from "./OnboardTenants/hooks/useOnboardingProcess";
import { getRememberedTenantId, rememberTenantId } from "../utils/tenantSelectionCookie";
import {
  applyEnrichmentPreviewFieldEdit,
  createEnrichmentRuleDetail,
  evaluateEnrichmentRules,
  getEnrichmentPreviewErrorMessage,
  getEnrichmentPreviewSourceFieldEdit,
  requestEnrichmentPreview,
} from "../utils/enrichmentPreview";
import EnrichmentPreviewSourceEditor from "./EnrichmentPreviewSourceEditor";
import LogAgents from "./LogAgents";
import CloseIcon from "@mui/icons-material/Close";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { Tooltip } from "@mui/material";
import ImporterTestsTab from "./ImporterTestsTab";

const ImporterStatusTab = ({ apiKey, tenants: externalTenants, selectedTenantId, setSelectedTenantId }) => {
  const isAdmin = isAdminUser(getUserEmail());
  const autoLoadedTenantIdRef = useRef("");
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [monitorInput, setMonitorInput] = useState("");
  const [isBlinking, setIsBlinking] = useState(false);
  const [actionType, setActionType] = useState("monitor");
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [skipWebScraper, setSkipWebScraper] = useState(true);
  const [forceSkipWebScrapeConfig, setForceSkipWebScrapeConfig] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [webScrapeIndexName, setWebScrapeIndexName] = useState("");
  const [processesList, setProcessesList] = useState([]);
  const [totalProcesses, setTotalProcesses] = useState(0);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedTraceId, setSelectedTraceId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const [timeFilter, setTimeFilter] = useState("all");
  const [importWarningDialogOpen, setImportWarningDialogOpen] = useState(false);
  const [originalIndexName, setOriginalIndexName] = useState("");
  const [startingImport, setStartingImport] = useState(false);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedLogTraceId, setSelectedLogTraceId] = useState("");
  const [overviewModalOpen, setOverviewModalOpen] = useState(false);
  const [tenantOverview, setTenantOverview] = useState(null);
  const [tenantImportSettings, setTenantImportSettings] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [feedFileSize, setFeedFileSize] = useState(null);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [loadingCsvPreview, setLoadingCsvPreview] = useState(false);
  const [csvSearchQuery, setCsvSearchQuery] = useState("");
  const [csvSelectedColumns, setCsvSelectedColumns] = useState([]);
  const [csvVisibleRows, setCsvVisibleRows] = useState(100);
  const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
  const [jsonPreviewData, setJsonPreviewData] = useState(null);
  const [loadingJsonPreview, setLoadingJsonPreview] = useState(false);
  const [jsonSearchQuery, setJsonSearchQuery] = useState("");
  const [jsonVisibleItems, setJsonVisibleItems] = useState(20);
  const [testRulesProductId, setTestRulesProductId] = useState("");
  const [testRulesResults, setTestRulesResults] = useState(null);
  const [testRulesRawProduct, setTestRulesRawProduct] = useState(null);
  const [testRulesLoading, setTestRulesLoading] = useState(false);
  const [ruleDetailRetesting, setRuleDetailRetesting] = useState(false);
  const [ruleDetailOpen, setRuleDetailOpen] = useState(false);
  const [ruleDetailData, setRuleDetailData] = useState(null);

  const ClickableValue = ({ value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      if (!value || value === "N/A") return;
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <Tooltip title={copied ? "Copied!" : "Click to copy"}>
        <Box
          component="span"
          onClick={handleCopy}
          sx={{
            cursor: value && value !== "N/A" ? "pointer" : "default",
            transition: "all 0.2s",
            "&:hover": {
              textDecoration: value && value !== "N/A" ? "underline" : "none",
              color: value && value !== "N/A" ? "primary.main" : "inherit",
              bgcolor: value && value !== "N/A" ? "rgba(25, 118, 210, 0.04)" : "transparent",
            },
          }}
        >
          {value || "N/A"}
        </Box>
      </Tooltip>
    );
  };

  const {
    onboardingStatus,
    processesList: monitoredProcessesList,
    finishedCounts,
    setOnboardingStatus,
    setProcessesList: setMonitoredProcessesList,
    startMonitoringReport,
    fetchProcessProgressOnce,
    stopMonitoring,
  } = useOnboardingProcess(apiKey);

  const areSubprocessesFinished = React.useMemo(() => {
    if (!monitoredProcessesList || monitoredProcessesList.length === 0) return false;
    return monitoredProcessesList.every(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      return s === "finished" || s === "failed" || s === "error";
    });
  }, [monitoredProcessesList]);

  const hasSubprocessesError = React.useMemo(() => {
    if (!monitoredProcessesList || monitoredProcessesList.length === 0) return false;
    return monitoredProcessesList.some(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      return s === "failed" || s === "error";
    });
  }, [monitoredProcessesList]);

  const hasSubprocessesUnresponsive = React.useMemo(() => {
    if (!monitoredProcessesList || monitoredProcessesList.length === 0) return false;
    return monitoredProcessesList.some(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      if (s === "finished" || s === "failed" || s === "error") return false;
      if (!p.startTime) return false;
      const start = new Date(p.startTime).getTime();
      return !isNaN(start) && (Date.now() - start) > 14400000;
    });
  }, [monitoredProcessesList]);

  const hasSubprocessesInProgress = React.useMemo(() => {
    if (!monitoredProcessesList || monitoredProcessesList.length === 0) return false;
    return monitoredProcessesList.some(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      return s === "inprogress" || s === "in progress" || s === "started";
    });
  }, [monitoredProcessesList]);

  useEffect(() => {
    if (!monitoredProcessesList || monitoredProcessesList.length === 0 || !selectedReportId) return;

    let derivedStatus = null;
    const hasError = monitoredProcessesList.some(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      return s === "failed" || s === "error";
    });
    const hasInProgress = monitoredProcessesList.some(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      return s === "inprogress" || s === "in progress" || s === "started";
    });
    const allFinished = monitoredProcessesList.every(p => {
      const s = (p.status || p.statusImport || "").toLowerCase();
      return s === "finished" || s === "completed";
    });

    if (hasError) {
      derivedStatus = "Error";
    } else if (hasInProgress) {
      derivedStatus = "InProgress";
    } else if (allFinished) {
      derivedStatus = "Finished";
    }

    if (derivedStatus) {
      setProcessesList(prev => prev.map(p => {
        const id = p.id || p.reportId;
        if (id !== selectedReportId) return p;
        const currentStatus = (p.status || "").toLowerCase();
        if (currentStatus === "unresponsive" || currentStatus === derivedStatus.toLowerCase()) return p;
        return { ...p, status: derivedStatus };
      }));
    }
  }, [monitoredProcessesList, selectedReportId]);

  useEffect(() => {
    const fetchTenants = async () => {
      setLoadingTenants(true);
      try {
        const response = await axiosInstance.get("/tenant/alltenants", {
          headers: { "t-api-key": apiKey }
        });
        const tenantList = response.data || [];
        setTenants(tenantList);

        const rememberedTenantId = getRememberedTenantId();
        if (rememberedTenantId) {
          const matchedTenant = tenantList.find((tenant) => tenant.id === rememberedTenantId);
          if (matchedTenant) {
            setSelectedTenant((prev) => (prev?.id === matchedTenant.id ? prev : matchedTenant));
          }
        }
      } catch (error) {
        console.error("Error fetching tenants:", error);
      } finally {
        setLoadingTenants(false);
      }
    };
    fetchTenants();
  }, [apiKey]);

  useEffect(() => {
    if (!selectedTenantId) return;

    const tenantList = tenants.length > 0 ? tenants : (externalTenants || []);
    const matchedTenant = tenantList.find((t) => t.id === selectedTenantId);
    if (matchedTenant) {
      setSelectedTenant((currentTenant) =>
        currentTenant === matchedTenant ? currentTenant : matchedTenant
      );
    }
  }, [selectedTenantId, tenants, externalTenants]);

  useEffect(() => {
    setMonitorInput(selectedTenant?.elasticIndexName || "");
    setOriginalIndexName(selectedTenant?.elasticIndexName || "");
    setWebScrapeIndexName(selectedTenant?.webScrapeIndexName || "");
  }, [selectedTenant]);

  useEffect(() => {
    if (!selectedTenant?.id) {
      setForceSkipWebScrapeConfig(false);
      return;
    }
    axiosInstance
      .get(`/tenant/${selectedTenant.id}`, { headers: { "t-api-key": apiKey } })
      .then((res) => {
        const featureConfigs = res.data?.tenantAiProfile?.featureConfigurations || {};
        const flag = featureConfigs.forceSkipWebScrape;
        setForceSkipWebScrapeConfig(flag === true || flag === "true");
      })
      .catch(() => setForceSkipWebScrapeConfig(false));
  }, [selectedTenant?.id, apiKey]);

  useEffect(() => {
    const tenantId = selectedTenant?.id || "";
    if (!tenantId) return;
    if (autoLoadedTenantIdRef.current === tenantId) return;

    autoLoadedTenantIdRef.current = tenantId;
    stopMonitoring();
    fetchHeaderOnboard(true).then((processes) => {
      if (processes && processes.length > 0) {
        handleProcessClick(processes[0]);
      }
    });
  }, [selectedTenant?.id]);

  const fetchHeaderOnboard = async (isBackgroundRefresh = false, pageNum = page) => {
    if (!selectedTenant) return [];

    if (!isBackgroundRefresh) setLoadingProcesses(true);
    try {
      const response = await axiosInstance.get(
        `/onboardingTenant/header-onboard/${encodeURIComponent(selectedTenant.id)}?page=${pageNum + 1}`,
        { headers: { "t-api-key": apiKey } }
      );
      const data = response.data;
      const items = data.items || data;
      const total = data.totalItems ?? (Array.isArray(data) ? data.length : 0);
      const serverPage = data.page != null ? data.page - 1 : pageNum;
      const sorted = [...items].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      setProcessesList(sorted);
      setTotalProcesses(total);
      setPage(serverPage);
      return sorted;
    } catch (error) {
      console.error("Error fetching header-onboard:", error);
      setProcessesList([]);
      setTotalProcesses(0);
      return [];
    } finally {
      if (!isBackgroundRefresh) setLoadingProcesses(false);
    }
  };


  const handleStartImport = async () => {
    if (!selectedTenant || !monitorInput) return;

    console.log("axiosInstanceImporter baseURL:", axiosInstanceImporter.defaults.baseURL);

    setStartingImport(true);

    // Fire and forget - don't wait for response
    axiosInstanceImporter.post(
      "ImportStoreWithoutDeletingFromBackground",
      {
        tenantId: selectedTenant.id,
        indexName: monitorInput,
        WebScrapeIndexName: webScrapeIndexName,
        ForceSkipScrape: skipWebScraper,
        Preview: previewMode
      },
      { headers: { "t-api-key": apiKey } }
    ).catch(error => {
      console.error("Error starting import:", error);
      setStartingImport(false);
    });

    // Refresh the processes list after 4 seconds
    setTimeout(() => {
      fetchHeaderOnboard(true);
      setStartingImport(false);
    }, 4000);
  };

  const handleStartNightlyUpdate = () => {
    if (!selectedTenant || !monitorInput) return;

    setStartingImport(true);

    // Fire and forget - don't wait for response
    axiosInstanceImporter.post(
      "/Products/process-products-update",
      {
        TenantId: selectedTenant.id,
        IndexName: monitorInput,
        WebScrapeIndexName: webScrapeIndexName,
        ForceSkipScrape: skipWebScraper,
        FromShopifyUpdate: false,
        IsManualTrigger: true,
        Preview: previewMode
      },
      { headers: { "t-api-key": apiKey } }
    ).catch(error => {
      console.error("Error starting nightly update:", error);
      setStartingImport(false);
    });

    // Refresh the processes list after 4 seconds
    setTimeout(() => {
      fetchHeaderOnboard(true).then(processes => {
        setStartingImport(false);
        if (processes && processes.length > 0) {
          handleProcessClick(processes[0]);
        }
      }).catch(err => {
        console.error("Refresh failed:", err);
        setStartingImport(false);
      });
    }, 4000);
  };

  const handleStartWebscraper = () => {
    if (!selectedTenant || !monitorInput) return;

    setStartingImport(true);

    axiosInstanceWebScraper.post(
      "/ProccessUnscrapeProduct",
      {
        indexName: monitorInput,
        tenantId: selectedTenant.id,
        webScrapeIndex: webScrapeIndexName || monitorInput,
        runAll: true
      }
    ).catch(error => {
      console.error("Error starting webscraper:", error);
      setStartingImport(false);
    });

    setTimeout(() => {
      fetchHeaderOnboard(true);
      setStartingImport(false);
    }, 4000);
  };

  const handleAction = () => {
    if (actionType === "monitor") {
      fetchHeaderOnboard();
    } else if (actionType === "import") {
      const indexNameChanged = monitorInput !== originalIndexName;

      // Show warning if index name unchanged OR if changed but web scraper not skipped
      if (!indexNameChanged || (indexNameChanged && !skipWebScraper)) {
        setImportWarningDialogOpen(true);
      } else {
        // Index name changed AND web scraper skipped - run without warning
        handleStartImport();
      }
    } else if (actionType === "nightly") {
      if (!skipWebScraper) {
        setImportWarningDialogOpen(true);
      } else {
        handleStartNightlyUpdate();
      }
    } else if (actionType === "webscraper") {
      handleStartWebscraper();
    }
  };

  const handleImportConfirm = () => {
    setImportWarningDialogOpen(false);
    if (actionType === "import") {
      handleStartImport();
    } else if (actionType === "nightly") {
      handleStartNightlyUpdate();
    }
  };

  const handleImportCancel = () => {
    setImportWarningDialogOpen(false);
  };

  const handleTenantChange = (_, value) => {
    autoLoadedTenantIdRef.current = "";
    setSelectedTenant(value);
    rememberTenantId(value?.id || "");
    if (typeof setSelectedTenantId === "function") {
      setSelectedTenantId(value?.id || "");
    }
    setOnboardingStatus(null);
    setMonitoredProcessesList(null);
    setProcessesList([]);
    setPage(0);
    setSelectedReportId(null);
    setSelectedTraceId(null);
    stopMonitoring();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchHeaderOnboard(false, newPage);
  };

  const handleProcessClick = (process) => {
    const status = (process.status || 'Unknown').toLowerCase();
    const reportId = process.id || process.reportId;
    setSelectedReportId(reportId);
    setSelectedTraceId(process.traceId);

    if (status === 'finished' || status === 'completed') {
      stopMonitoring();
      fetchProcessProgressOnce({
        reportId: reportId,
        tenantId: process.tenantId,
      });
    } else {
      startMonitoringReport({
        reportId: reportId,
        tenantId: process.tenantId,
        onPoll: () => {
          if (selectedTenant?.name) {
            fetchHeaderOnboard(true);
          }
        },
      });
    }
  };

  const handleDeleteClick = (event, process) => {
    event.stopPropagation();
    setProcessToDelete(process);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!processToDelete) return;

    const deletedId = processToDelete.id || processToDelete.reportId;

    try {
      await axiosInstance.delete(
        `/onboardingTenant/delete-event/${processToDelete.tenantId || selectedTenant?.id}/${deletedId}`,
        { headers: { "t-api-key": apiKey } }
      );

      setProcessesList((prev) => prev.filter((p) => (p.id || p.reportId) !== deletedId));
      stopMonitoring();
      setSelectedReportId(null);
      setSelectedTraceId(null);
      setOnboardingStatus(null);
      setMonitoredProcessesList(null);
    } catch (error) {
      console.error("Error deleting process:", error);
    }

    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  };

  const handleOpenLogModal = (traceId) => {
    if (!traceId) return;
    setSelectedLogTraceId(traceId);
    setLogModalOpen(true);
  };

  const handleOpenOverview = async (event) => {
    if (!selectedTenant) return;
    setOverviewModalOpen(true);
    setLoadingOverview(true);
    setFeedFileSize(null);
    try {
      const [tenantRes, importRes] = await Promise.allSettled([
        axiosInstance.get(`/tenant/${selectedTenant.id}`, {
          headers: { "t-api-key": apiKey },
        }),
        axiosInstance.get(`/tenant-import/enrichment-settings/${selectedTenant.id}`, {
          headers: { "t-api-key": apiKey },
        }),
      ]);
      setTenantOverview(tenantRes.status === "fulfilled" ? tenantRes.value.data : null);
      setTenantImportSettings(
        importRes.status === "fulfilled" ? importRes.value.data?.importEnrichRules : null
      );

      const feedUrl = tenantRes.status === "fulfilled"
        ? tenantRes.value.data?.dataFeedConnector?.configurations?.dataFeedUrl
        : null;
      if (feedUrl) {
        try {
          const headRes = await fetch(feedUrl, { method: "HEAD" });
          const contentLength = headRes.headers.get("content-length");
          if (contentLength) setFeedFileSize(Number(contentLength));
        } catch {
          setFeedFileSize(null);
        }
      }
    } catch (error) {
      console.error("Error fetching tenant overview:", error);
      setTenantOverview(null);
      setTenantImportSettings(null);
    } finally {
      setLoadingOverview(false);
    }
  };

  const handleCloseOverview = () => {
    setOverviewModalOpen(false);
    setTenantOverview(null);
    setTenantImportSettings(null);
    setTestRulesProductId("");
    setTestRulesResults(null);
    setTestRulesRawProduct(null);
    setRuleDetailRetesting(false);
  };

  const handleOpenCsvPreview = async () => {
    const feedUrl = tenantOverview?.dataFeedConnector?.configurations?.dataFeedUrl;
    if (!feedUrl) return;
    setCsvPreviewOpen(true);
    setLoadingCsvPreview(true);
    try {
      const response = await fetch(feedUrl);
      const text = await response.text();
      const lines = text.split("\n").filter((line) => line.trim() !== "");
      const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
      const idColIndex = headers.findIndex((h) => h.toLowerCase() === "id");
      const rows = lines.slice(1).map((line) => {
        const values = line.split(",").map((v, idx) => {
          let val = v.trim().replace(/^"|"$/g, "");
          if (idx === idColIndex && /^[\d.]+[eE]\+?\d+$/.test(val)) {
            val = Number(val).toLocaleString("fullwide", { useGrouping: false });
          }
          return val;
        });
        return values;
      });
      setCsvPreviewData({ headers, rows });
    } catch (error) {
      console.error("Error fetching CSV preview:", error);
      setCsvPreviewData(null);
    } finally {
      setLoadingCsvPreview(false);
    }
  };

  const handleCloseCsvPreview = () => {
    setCsvPreviewOpen(false);
    setCsvPreviewData(null);
    setCsvSearchQuery("");
    setCsvSelectedColumns([]);
    setCsvVisibleRows(100);
  };

  const handleOpenJsonPreview = async () => {
    const feedUrl = tenantOverview?.dataFeedConnector?.configurations?.dataFeedUrl;
    if (!feedUrl) return;
    const dataType = tenantOverview?.dataFeedConnector?.configurations?.dataType?.toLowerCase();

    setJsonPreviewOpen(true);
    setLoadingJsonPreview(true);
    try {
      const response = await fetch(feedUrl);
      if (dataType === "xml") {
        const text = await response.text();
        setJsonPreviewData(text);
      } else {
        const json = await response.json();
        setJsonPreviewData(json);
      }
    } catch (error) {
      console.error("Error fetching preview:", error);
      setJsonPreviewData(null);
    } finally {
      setLoadingJsonPreview(false);
    }
  };

  const handleCloseJsonPreview = () => {
    setJsonPreviewOpen(false);
    setJsonPreviewData(null);
    setJsonSearchQuery("");
    setJsonVisibleItems(20);
  };

  const handleTestRules = async () => {
    if (!selectedTenant?.id || !tenantImportSettings?.rules?.length) return;

    setTestRulesLoading(true);
    setTestRulesResults(null);
    setTestRulesRawProduct(null);

    try {
      const { results, rawProduct } = await requestEnrichmentPreview(
        selectedTenant.id,
        tenantImportSettings.rules,
        testRulesProductId,
        {
          feedConfigurations: tenantOverview?.dataFeedConnector?.configurations,
          productIdFieldName: tenantImportSettings.productIdFieldName,
        }
      );
      setTestRulesResults(results);
      setTestRulesRawProduct(rawProduct || null);
    } catch (error) {
      console.error("Error testing rules:", error);
      const message = getEnrichmentPreviewErrorMessage(error);
      setTestRulesResults(
        tenantImportSettings.rules.map(() => ({ error: message }))
      );
    } finally {
      setTestRulesLoading(false);
    }
  };

  const handleRetestRule = async (rule, sourceFieldEdit) => {
    setRuleDetailRetesting(true);
    try {
      if (sourceFieldEdit !== undefined) {
        return evaluateEnrichmentRules(applyEnrichmentPreviewFieldEdit(testRulesRawProduct, sourceFieldEdit), [rule])[0];
      }
      const { results } = await requestEnrichmentPreview(
        selectedTenant.id,
        [rule],
        testRulesProductId,
        {
          feedConfigurations: tenantOverview?.dataFeedConnector?.configurations,
          productIdFieldName: tenantImportSettings.productIdFieldName,
        }
      );
      return results[0];
    } catch (error) {
      return { error: getEnrichmentPreviewErrorMessage(error) };
    } finally {
      setRuleDetailRetesting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'InProgress':
      case 'In Progress':
        return '#1976d2';
      case 'Started':
        return '#d4a300';
      case 'Finished':
        return 'lightgreen';
      case 'Failed':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredProcessesList = React.useMemo(() => {
    if (!processesList) return [];

    const now = new Date();
    let timeLimit = null;

    switch (timeFilter) {
      case '10min': timeLimit = new Date(now.getTime() - 10 * 60 * 1000); break;
      case '30min': timeLimit = new Date(now.getTime() - 30 * 60 * 1000); break;
      case '1hour': timeLimit = new Date(now.getTime() - 60 * 60 * 1000); break;
      case '2hour': timeLimit = new Date(now.getTime() - 2 * 60 * 60 * 1000); break;
      case '12hour': timeLimit = new Date(now.getTime() - 12 * 60 * 60 * 1000); break;
      case '1day': timeLimit = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
      case '2days': timeLimit = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000); break;
      case '1week': timeLimit = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
      default: timeLimit = null;
    }

    if (!timeLimit) return processesList;

    return processesList.filter(p => {
      if (!p.timestamp) return false;
      const pTime = new Date(p.timestamp);
      return pTime >= timeLimit;
    });
  }, [processesList, timeFilter]);

  // Clear selected process when filter hides all items
  useEffect(() => {
    if (filteredProcessesList.length === 0 && selectedReportId) {
      setSelectedReportId(null);
      stopMonitoring();
    }
  }, [filteredProcessesList]);

  const paginatedProcesses = filteredProcessesList;

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const highlightMatch = (text, query) => {
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <Box key={i} component="span" sx={{ backgroundColor: "#fff176", fontWeight: "bold" }}>
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  const getImportWarningMessage = () => {
    const indexNameChanged = monitorInput !== originalIndexName;

    if (actionType === "nightly") {
      if (!skipWebScraper) {
        return (
          <p><b style={{ marginTop: '16px', color: 'orange' }}>WARNING WEB SCRAPER</b> <br />Skip scraper is unchecked. It will run the web scraper during this import.</p>
        );
      }
      return "";
    }

    if (!indexNameChanged) {
      return (
        <>
          <p><b style={{ marginTop: '16px', color: 'orange' }}>WARNING INDEX NAME UNCHANGED</b> <br />Data will be cleared before the feed begins re-importing.<br />Live clients will not function until the feed has re-indexed.</p>
          <small>For live clients, it is recommended to change the "Index Name" and import into a new database, then swap the live client index name once the import has fully completed.</small>
          {!skipWebScraper && (
            <p><b style={{ marginTop: '16px', color: 'orange' }}>WARNING WEB SCRAPER</b> <br />Skip scraper is unchecked. It will run the web scraper during this import.</p>
          )}
        </>
      );
    } else if (!skipWebScraper) {
      return (
        <>
          <p><b style={{ marginTop: '16px', color: 'orange' }}>WARNING WEB SCRAPER</b> <br />Skip scraper is unchecked. It will run the web scraper during this import.</p>
        </>
      );
    }
    return "";
  };

  const sectionPaperSx = {
    p: { xs: 2, md: 2.5 },
    mt: 3,
    borderRadius: "22px",
    border: "1px solid rgba(255, 132, 76, 0.10)",
    boxShadow: "0 24px 60px rgba(236, 123, 55, 0.08)",
    backgroundColor: "rgba(255,255,255,0.92)",
  };

  const primaryButtonSx = {
    minHeight: 44,
    px: 2.5,
    borderRadius: "14px",
    textTransform: "none",
    fontWeight: 700,
    color: "#fff",
    background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
    boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
    "&:hover": {
      background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)",
      boxShadow: "0 18px 36px rgba(255, 139, 71, 0.34)",
    },
  };

   return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 } }}>
      <Helmet>
        <title>Importer Status</title>
      </Helmet>
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2.25,
          mb: 3.5,
        }}
      >
        <Box
          sx={{
            width: { xs: 56, md: 64 },
            height: { xs: 56, md: 64 },
            borderRadius: "20px",
            background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
            boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <CloudSyncOutlinedIcon sx={{ fontSize: 30 }} />
        </Box>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "2rem", md: "2.55rem" },
              lineHeight: 1.05,
              fontWeight: 800,
              color: "#202531",
              letterSpacing: "-0.03em",
              mb: 0.8,
            }}
          >
            Importer Status
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#6b7280",
              fontWeight: 500,
            }}
          >
            Monitor runs, trigger imports, inspect processes, and follow onboarding progress.
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={activeSubTab}
        onChange={(e, v) => setActiveSubTab(v)}
        sx={{
          mb: 3,
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "999px",
            background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
          },
        }}
      >
        <Tab
          icon={<CloudSyncOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Import Operations"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "#4b5563",
            gap: 0.75,
            "&.Mui-selected": { color: "#f97316" },
          }}
        />
        <Tab
          icon={<ScienceOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Test Mappings"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "#4b5563",
            gap: 0.75,
            "&.Mui-selected": { color: "#f97316" },
          }}
        />
      </Tabs>

      {activeSubTab === 0 && (
      <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3.25,
          borderRadius: "24px",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          backgroundColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
        }}
      >
        <Box display="flex" gap={1.5} mb={1.5} alignItems="center" flexWrap="wrap">
          <TenantAutocomplete
            tenants={tenants}
            loading={loadingTenants}
            value={selectedTenant}
            onChange={handleTenantChange}
            label="Select Tenant"
            sx={{ flex: 1.5, minWidth: 250 }}
            size="small"
          />

          <Tooltip title="Tenant Overview">
            <span>
              <IconButton
                onClick={handleOpenOverview}
                disabled={!selectedTenant}
                size="small"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "16px",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  backgroundColor: "#fff",
                  boxShadow: "0 14px 30px rgba(236, 123, 55, 0.08)",
                  color: "#4b5563",
                  "&:hover": {
                    backgroundColor: "#fff7f1",
                    color: "#f97316",
                  },
                }}
              >
                <Info size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <TextField
            label="Index Name"
            value={monitorInput}
            onChange={(e) => setMonitorInput(e.target.value)}
            size="small"
            sx={{
              flex: 2,
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                minHeight: 56,
                borderRadius: "16px",
                backgroundColor: "#fff",
                "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
              },
            }}
          />

          {(actionType === "import" || actionType === "nightly") && !skipWebScraper && (
            <TextField
              label="Web Scrape Index Name"
              value={webScrapeIndexName}
              onChange={(e) => setWebScrapeIndexName(e.target.value)}
              size="small"
              sx={{
                flex: 2,
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  minHeight: 56,
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                  "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                  "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                },
              }}
            />
          )}
        </Box>

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={0.5}>
            <RadioGroup
              row
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
            >
              <FormControlLabel value="monitor" control={<Radio size="small" />} label="Monitor" />
              <FormControlLabel value="import" control={<Radio size="small" />} label="Run Import" />
              <FormControlLabel value="nightly" control={<Radio size="small" />} label="Nightly Update" />
              <FormControlLabel value="webscraper" control={<Radio size="small" />} label="Webscraper" />
            </RadioGroup>

            {(actionType === "import" || actionType === "nightly") && !forceSkipWebScrapeConfig && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipWebScraper}
                    onChange={(e) => setSkipWebScraper(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Skip Web Scraper</Typography>}
              />
            )}

            {(actionType === "import" || actionType === "nightly") && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={previewMode}
                    onChange={(e) => setPreviewMode(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Preview (Use prompt's draft if any)</Typography>}
              />
            )}
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<Monitor size={16} />}
            onClick={handleAction}
            disabled={startingImport}
            sx={primaryButtonSx}
          >
            {actionType === "monitor" ? "Load Processes" : actionType === "import" ? "Run Import" : actionType === "nightly" ? "Nightly Update" : "Run Webscraper"}
          </Button>
        </Box>
      </Paper>

      {startingImport && (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mb={2} p={1.75} sx={{ backgroundColor: 'rgba(255, 247, 241, 0.88)', borderRadius: 2, border: '1px solid rgba(255, 132, 76, 0.14)' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ color: "#f97316", fontWeight: 700 }}>
            Starting import process...
          </Typography>
        </Box>
      )}

      {loadingProcesses && (
        <Box display="flex" justifyContent="center" alignItems="center" my={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loadingProcesses && processesList.length > 0 && (
        <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Processes
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.85rem' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="10min">Last 10 min</MenuItem>
                <MenuItem value="30min">Last 30 min</MenuItem>
                <MenuItem value="1hour">Last 1 hour</MenuItem>
                <MenuItem value="2hour">Last 2 hours</MenuItem>
                <MenuItem value="12hour">Last 12 hours</MenuItem>
                <MenuItem value="1day">Last 1 day</MenuItem>
                <MenuItem value="2days">Last 2 days</MenuItem>
                <MenuItem value="1week">Last 1 week</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "18px", border: "1px solid rgba(255, 132, 76, 0.10)" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 700, fontSize: '0.8rem', py: 1.35, color: "#f97316", background: "linear-gradient(180deg, rgba(255, 244, 238, 0.98), rgba(255, 247, 243, 0.98))", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" } }}>
                  <TableCell sx={{ width: 20, p: 0 }}></TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Index Name</TableCell>
                  <TableCell>Triggered By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProcesses.map((process) => {
                  const isCurrentSelected = selectedReportId === (process.id || process.reportId);
                  const status = (process.status || '').toLowerCase();

                  let rowBgColor = 'transparent';
                  let rowHoverColor = '#fafafa';
                  let rowTextColor = 'inherit';
                  let rowBorderColor = '#bdbdbd';

                  if (status === 'failed' || status === 'error') {
                    rowBgColor = isCurrentSelected ? '#ffcdd2' : '#ffebee';
                    rowHoverColor = '#ef9a9a';
                    rowTextColor = '#b71c1c';
                    rowBorderColor = '#f44336';
                  } else if (status === 'finished' || status === 'completed') {
                    rowBgColor = isCurrentSelected ? '#a5d6a7' : '#c8e6c9';
                    rowHoverColor = '#81c784';
                    rowTextColor = '#1b5e20';
                    rowBorderColor = '#4caf50';
                  } else if (status === 'inprogress' || status === 'in progress' || status === 'started') {
                    rowBgColor = isCurrentSelected ? '#fff3c4' : '#fff8e1';
                    rowHoverColor = '#fff3c4';
                    rowTextColor = '#6d4c00';
                    rowBorderColor = '#e0b014';
                  } else if (status === 'not started' || status === 'pending') {
                    rowBgColor = isCurrentSelected ? '#e3f2fd' : 'rgba(158, 158, 158, 0.04)';
                    rowHoverColor = 'rgba(158, 158, 158, 0.12)';
                    rowTextColor = '#616161';
                    rowBorderColor = '#90caf9';
                  } else if (status === 'unresponsive') {
                    rowBgColor = isCurrentSelected ? '#e0e0e0' : '#f5f5f5';
                    rowHoverColor = '#e0e0e0';
                    rowTextColor = '#424242';
                    rowBorderColor = '#757575';
                  } else if (isCurrentSelected) {
                    rowBgColor = '#e3f2fd';
                    rowHoverColor = '#bbdefb';
                  }

                  return (
                    <TableRow
                      key={process.id || process.reportId}
                      hover
                      onClick={() => handleProcessClick(process)}
                      sx={{
                        cursor: 'pointer',
                        background: isCurrentSelected ? `${rowBgColor} !important` : rowBgColor,
                        borderLeft: isCurrentSelected ? `5px solid ${rowBorderColor}` : `4px solid ${rowBorderColor}`,
                        transition: 'background 0.3s ease, border-color 0.3s ease',
                        ...(isCurrentSelected && {
                          boxShadow: `inset 0 2px 0 0 ${rowBorderColor}, inset 0 -2px 0 0 ${rowBorderColor}, 0 2px 8px rgba(0,0,0,0.1)`,
                        }),
                        '& .MuiTableCell-root': {
                          background: 'inherit',
                          color: rowTextColor,
                          fontSize: '0.82rem',
                          py: isCurrentSelected ? 1.2 : 0.8,
                          fontWeight: isCurrentSelected ? 700 : 400,
                        },
                        '&:hover': {
                          background: rowHoverColor,
                        },
                      }}
                    >
                      <TableCell sx={{ width: 20, p: 0, textAlign: 'center' }}>
                        {isCurrentSelected && <span style={{ fontSize: '0.7rem', color: rowBorderColor }}>▶</span>}
                      </TableCell>
                      <TableCell>
                        {process.id}
                      </TableCell>
                      <TableCell>{process.status || '—'}</TableCell>
                      <TableCell>
                        {process.type}
                        {process.type && (process.type.toLowerCase() === 'log' || process.type.toLowerCase() === 'info') && (process.lastLogMessage || process.lasLogMessage) && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {process.lastLogMessage || process.lasLogMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatTimestamp(process.timestamp)}</TableCell>
                      <TableCell>{process.IndexName || process.indexName || '—'}</TableCell>
                      <TableCell>{process.triggeredBy || '—'}</TableCell>
                      <TableCell>
                        {isAdmin && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => handleDeleteClick(e, process)}
                          sx={{ '&:hover': { backgroundColor: '#ffebee' } }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalProcesses}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5]}
            />
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete process ID {processToDelete?.id || processToDelete?.reportId}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importWarningDialogOpen}
        onClose={handleImportCancel}
      >
        <DialogTitle>Import Warning</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {getImportWarningMessage()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleImportConfirm} color="warning" variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      <Dialog
        open={overviewModalOpen}
        onClose={handleCloseOverview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Info size={18} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                Tenant Overview
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                Quick tenant metadata, feed details, and import settings.
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseOverview}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#fff7f1",
                color: "#f97316",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {loadingOverview ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : tenantOverview ? (
            <Box>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: "22px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  backgroundColor: "rgba(255,255,255,0.82)",
                  boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                }}
              >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                    ID
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                    <ClickableValue value={tenantOverview.id} />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                    Name
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                    <ClickableValue value={tenantOverview.name} />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                    Website URL
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                    <ClickableValue value={tenantOverview.websiteUrl} />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                    Elastic Index
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                    <ClickableValue value={tenantOverview.tenantAiProfile?.elasticIndexName} />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                    Policy Index
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                    <ClickableValue value={tenantOverview.tenantAiProfile?.policyIndexName} />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                        Data Feed
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500, wordBreak: "break-word" }}>
                        <ClickableValue value={tenantOverview.dataFeedConnector?.configurations?.dataFeed} />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                        Data Type
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500, wordBreak: "break-word" }}>
                        <ClickableValue value={tenantOverview.dataFeedConnector?.configurations?.dataType} />
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.4 }}>
                    Data Feed URL
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ wordBreak: "break-word", overflowWrap: "anywhere", color: "#202531", fontWeight: 500 }}>
                    <ClickableValue value={tenantOverview.dataFeedConnector?.configurations?.dataFeedUrl} />
                    {tenantOverview.dataFeedConnector?.configurations?.dataFeedUrl && (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ml: 1, verticalAlign: "middle" }}>
                        {tenantOverview.dataFeedConnector?.configurations?.dataType?.toLowerCase() === "csv" && (
                          <Tooltip title="Preview CSV">
                            <IconButton size="small" onClick={handleOpenCsvPreview} sx={{ color: "#f97316" }}>
                              <Eye size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(tenantOverview.dataFeedConnector?.configurations?.dataFeed?.toLowerCase() === "http" ||
                          tenantOverview.dataFeedConnector?.configurations?.dataType?.toLowerCase() === "xml") &&
                          tenantOverview.dataFeedConnector?.configurations?.dataType?.toLowerCase() !== "csv" && (
                            <Tooltip title={`Preview ${tenantOverview.dataFeedConnector?.configurations?.dataType?.toUpperCase() || "Data"}`}>
                              <IconButton size="small" onClick={handleOpenJsonPreview} sx={{ color: "#f97316" }}>
                                <Eye size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                        <Tooltip title="Download file">
                          <IconButton
                            size="small"
                            sx={{ color: "#f97316" }}
                            component="a"
                            href={tenantOverview.dataFeedConnector.configurations.dataFeedUrl}
                            target="_blank"
                            download
                          >
                            <Download size={16} />
                          </IconButton>
                        </Tooltip>
                        {feedFileSize && (
                          <Typography variant="caption" color="text.secondary" component="span">
                            ({formatFileSize(feedFileSize)})
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Typography>
                </Grid>
              </Grid>
              </Box>

              <Box
                mt={3}
                sx={{
                  p: 2.25,
                  borderRadius: "22px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  backgroundColor: "rgba(255,255,255,0.82)",
                  boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                }}
              >
                <Typography variant="h6" mb={2} sx={{ fontWeight: 800, color: "#202531" }}>
                  Import Settings
                </Typography>
                {tenantImportSettings ? (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Product ID Field
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                          <ClickableValue value={tenantImportSettings.productIdFieldName} />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Title Field
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                          <ClickableValue value={tenantImportSettings.titleFieldName} />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Has Parent Product
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#202531", fontWeight: 500 }}>
                          {tenantImportSettings.hasParentProduct ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                    </Grid>
                    {tenantImportSettings.rules?.length > 0 && (
                      <Box mt={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Rules ({tenantImportSettings.rules.length})
                          </Typography>
                          <Box display="flex" gap={1} alignItems="center">
                            <TextField
                              size="small"
                              placeholder="Product ID (optional)"
                              value={testRulesProductId}
                              onChange={(event) => setTestRulesProductId(event.target.value)}
                              sx={{
                                width: 200,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "12px",
                                  backgroundColor: "#fff",
                                },
                              }}
                              inputProps={{ style: { fontSize: "0.75rem", padding: "4px 8px" } }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleTestRules}
                              disabled={testRulesLoading}
                              sx={{
                                whiteSpace: "nowrap",
                                fontSize: "0.7rem",
                                px: 1.5,
                                py: 0.5,
                                minWidth: 0,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: "#fff",
                                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                                boxShadow: "0 12px 24px rgba(255, 139, 71, 0.22)",
                              }}
                            >
                              {testRulesLoading ? <CircularProgress size={14} /> : "Test Rules"}
                            </Button>
                          </Box>
                        </Box>
                        {tenantImportSettings.rules.map((rule, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              mb: 1,
                              px: 1.5,
                              py: 1,
                              bgcolor: "rgba(255, 247, 241, 0.86)",
                              borderRadius: 2,
                              border: "1px solid rgba(255, 132, 76, 0.10)",
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip label={rule.type} size="small" variant="outlined" />
                            {rule.fieldName && (
                              <Typography variant="body2">{rule.fieldName}</Typography>
                            )}
                            {rule.script && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                  minWidth: 0,
                                }}
                                title={rule.script}
                              >
                                {rule.script}
                              </Typography>
                            )}
                            {testRulesResults && testRulesResults[idx] && (
                              <Chip
                                size="small"
                                label={(() => {
                                  const text = testRulesResults[idx].error || testRulesResults[idx].value;
                                  return text.length > 15 ? text.slice(0, 15) + "..." : text;
                                })()}
                                color={
                                  testRulesResults[idx].error ? "error" : "success"
                                }
                                onClick={() => {
                                  setRuleDetailData(createEnrichmentRuleDetail(rule, testRulesResults[idx], idx, testRulesRawProduct));
                                  setRuleDetailOpen(true);
                                }}
                                sx={{
                                  ml: "auto",
                                  maxWidth: 150,
                                  cursor: "pointer",
                                  "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  },
                                }}
                                title="Click to see full details"
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No import settings configured.
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No overview data available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {activeSubTab === 0 && (
      <>
      <Dialog
        open={ruleDetailOpen}
        onClose={() => setRuleDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Info size={18} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                Rule Detail
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                Inspect the rule, edit the script, and validate the result.
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setRuleDetailOpen(false)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#fff7f1",
                color: "#f97316",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {ruleDetailData && (
            <Box
              sx={{
                p: 2.25,
                borderRadius: "22px",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                backgroundColor: "rgba(255,255,255,0.82)",
                boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
              }}
            >
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Type</Typography>
                <Typography variant="body1" sx={{ color: "#202531", fontWeight: 500 }}>{ruleDetailData.rule.type}</Typography>
              </Box>
              {ruleDetailData.rule.fieldName && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Field Name</Typography>
                  <Typography variant="body1" sx={{ color: "#202531", fontWeight: 500 }}>{ruleDetailData.rule.fieldName}</Typography>
                </Box>
              )}
              <EnrichmentPreviewSourceEditor ruleDetailData={ruleDetailData} setRuleDetailData={setRuleDetailData} disabled={ruleDetailRetesting} />
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Script</Typography>
                <TextField
                  label="Rule script"
                  fullWidth
                  multiline
                  rows={4}
                  value={ruleDetailData.rule.script || ""}
                  onChange={(e) =>
                    setRuleDetailData((prev) => ({
                      ...prev,
                      rule: { ...prev.rule, script: e.target.value },
                    }))
                  }
                  InputProps={{
                    inputComponent: "textarea",
                    sx: {
                      fontFamily: "monospace",
                      fontSize: "0.9rem",
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                    },
                  }}
                  sx={{
                    mt: 0.75,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                  }}
                />
              </Box>
              <Box mb={2} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={ruleDetailRetesting || !!ruleDetailData.sourceFieldError}
                  onClick={async () => {
                    const result = await handleRetestRule(ruleDetailData.rule, getEnrichmentPreviewSourceFieldEdit(ruleDetailData));
                    setRuleDetailData((prev) => ({ ...prev, result }));
                  }}
                  sx={{
                    minHeight: 40,
                    px: 2.25,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 700,
                    color: "#f97316",
                    borderColor: "rgba(249, 115, 22, 0.26)",
                    backgroundColor: "rgba(255, 247, 241, 0.86)",
                    "&:hover": {
                      borderColor: "rgba(249, 115, 22, 0.42)",
                      backgroundColor: "rgba(255, 241, 233, 0.96)",
                    },
                  }}
                >
                  {ruleDetailRetesting ? <CircularProgress size={14} /> : "Re-test"}
                </Button>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Result</Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: ruleDetailData.result.error ? "rgba(254, 242, 242, 0.96)" : "rgba(240, 253, 244, 0.96)",
                    color: ruleDetailData.result.error ? "#dc2626" : "#15803d",
                    p: 1.5,
                    borderRadius: 2,
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    border: "1px solid",
                    borderColor: ruleDetailData.result.error ? "rgba(248, 113, 113, 0.30)" : "rgba(74, 222, 128, 0.30)",
                    mt: 0.75,
                  }}
                >
                  {ruleDetailData.result.error || ruleDetailData.result.value}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={csvPreviewOpen}
        onClose={handleCloseCsvPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Eye size={18} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                CSV Preview
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                Search rows, filter columns, and inspect feed contents.
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseCsvPreview}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#fff7f1",
                color: "#f97316",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {loadingCsvPreview ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : csvPreviewData ? (
            <Box
              sx={{
                p: 2.25,
                borderRadius: "22px",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                backgroundColor: "rgba(255,255,255,0.82)",
                boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search in CSV..."
                  value={csvSearchQuery}
                  onChange={(e) => setCsvSearchQuery(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 52,
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                  }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  options={csvPreviewData.headers}
                  value={csvSelectedColumns}
                  onChange={(_, newValue) => setCsvSelectedColumns(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Filter Columns" placeholder="Search Columns..." />
                  )}
                  sx={{
                    minWidth: 300,
                    flexShrink: 0,
                    "& .MuiOutlinedInput-root": {
                      minHeight: 52,
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                  }}
                  limitTags={2}
                />
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                  {csvPreviewData.rows.length} rows
                </Typography>
              </Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: "18px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  boxShadow: "0 16px 34px rgba(236, 123, 55, 0.06)",
                  overflow: "hidden",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-head": {
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          color: "#f97316",
                          background: "linear-gradient(180deg, rgba(255, 244, 238, 0.98), rgba(255, 247, 243, 0.98))",
                          borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
                        },
                      }}
                    >
                      {csvPreviewData.headers.map((header, idx) => (
                        (csvSelectedColumns.length === 0 || csvSelectedColumns.includes(header)) && (
                          <TableCell key={idx}>
                            {header}
                          </TableCell>
                        )
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const filtered = csvPreviewData.rows.filter((row) => {
                        if (!csvSearchQuery.trim()) return true;
                        const query = csvSearchQuery.toLowerCase();
                        const indicesToSearch = csvSelectedColumns.length > 0
                          ? csvSelectedColumns.map(col => csvPreviewData.headers.indexOf(col))
                          : csvPreviewData.headers.map((_, i) => i);

                        return indicesToSearch.some((idx) => row[idx]?.toLowerCase().includes(query));
                      });
                      const visible = csvSearchQuery.trim()
                        ? filtered
                        : filtered.slice(0, csvVisibleRows);
                      return visible.map((row, rowIdx) => (
                        <TableRow
                          key={rowIdx}
                          sx={{
                            backgroundColor: rowIdx % 2 === 0 ? "rgba(255,255,255,0.92)" : "rgba(255, 247, 241, 0.62)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 241, 233, 0.82)",
                            },
                          }}
                        >
                          {row.map((cell, cellIdx) => {
                            const header = csvPreviewData.headers[cellIdx];
                            if (csvSelectedColumns.length > 0 && !csvSelectedColumns.includes(header)) return null;
                            const isIdColumn = header?.toLowerCase() === "id";
                            return (
                              <TableCell key={cellIdx} sx={{ whiteSpace: "nowrap", borderBottom: "1px solid rgba(255, 132, 76, 0.08)" }}>
                                {isIdColumn ? (
                                  <span>{cell}</span>
                                ) : csvSearchQuery.trim() && cell.toLowerCase().includes(csvSearchQuery.toLowerCase())
                                  ? highlightMatch(cell, csvSearchQuery)
                                  : cell}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              {!csvSearchQuery.trim() && csvVisibleRows < csvPreviewData.rows.length && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setCsvVisibleRows((prev) => prev + 100)}
                    sx={{
                      minHeight: 42,
                      px: 2.5,
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#f97316",
                      borderColor: "rgba(249, 115, 22, 0.26)",
                      backgroundColor: "rgba(255, 247, 241, 0.86)",
                      "&:hover": {
                        borderColor: "rgba(249, 115, 22, 0.42)",
                        backgroundColor: "rgba(255, 241, 233, 0.96)",
                      },
                    }}
                  >
                    Load More ({Math.min(csvVisibleRows, csvPreviewData.rows.length)} / {csvPreviewData.rows.length})
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Failed to load CSV preview.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={jsonPreviewOpen}
        onClose={handleCloseJsonPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {tenantOverview?.dataFeedConnector?.configurations?.dataType?.toUpperCase() || "Data"} Preview
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseJsonPreview}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingJsonPreview ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : jsonPreviewData ? (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search in ${tenantOverview?.dataFeedConnector?.configurations?.dataType?.toUpperCase() || "data"}...`}
                  value={jsonSearchQuery}
                  onChange={(e) => setJsonSearchQuery(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {Array.isArray(jsonPreviewData) ? `${jsonPreviewData.length} items` : "1 object"}
                </Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: "65vh",
                  overflow: "auto",
                  borderRadius: 1,
                  "& pre": { margin: 0 },
                }}
              >
                {(() => {
                  const dataType = tenantOverview?.dataFeedConnector?.configurations?.dataType?.toLowerCase();
                  const isXml = dataType === "xml";

                  if (isXml) {
                    const text = typeof jsonPreviewData === "string" ? jsonPreviewData : "";
                    const lines = text.split("\n");
                    const filteredLines = jsonSearchQuery.trim()
                      ? lines.filter(line => line.toLowerCase().includes(jsonSearchQuery.toLowerCase()))
                      : lines;
                    const visibleText = jsonSearchQuery.trim()
                      ? filteredLines.join("\n")
                      : lines.slice(0, jsonVisibleItems * 20).join("\n"); // More lines for XML as they are usually shorter

                    return (
                      <>
                        <SyntaxHighlighter
                          language="xml"
                          style={vs2015}
                          showLineNumbers
                          wrapLongLines
                          customStyle={{ fontSize: "13px", borderRadius: "4px" }}
                        >
                          {visibleText}
                        </SyntaxHighlighter>
                        {!jsonSearchQuery.trim() && lines.length > jsonVisibleItems * 20 && (
                          <Box display="flex" justifyContent="center" mt={2}>
                            <Button
                              variant="outlined"
                              onClick={() => setJsonVisibleItems((prev) => prev + 50)}
                            >
                              Load More
                            </Button>
                          </Box>
                        )}
                        {jsonSearchQuery.trim() && (
                          <Typography variant="caption" color="text.secondary" mt={1} display="block">
                            {filteredLines.length} line{filteredLines.length !== 1 ? "s" : ""} matching &quot;{jsonSearchQuery}&quot;
                          </Typography>
                        )}
                      </>
                    );
                  }

                  const items = Array.isArray(jsonPreviewData) ? jsonPreviewData : [jsonPreviewData];
                  const filtered = jsonSearchQuery.trim()
                    ? items.filter((item) =>
                      JSON.stringify(item).toLowerCase().includes(jsonSearchQuery.toLowerCase())
                    )
                    : items;
                  const visible = filtered.slice(0, jsonVisibleItems);
                  const jsonStr = JSON.stringify(visible, null, 2);
                  return (
                    <>
                      <SyntaxHighlighter
                        language="json"
                        style={vs2015}
                        showLineNumbers
                        wrapLongLines
                        customStyle={{ fontSize: "13px", borderRadius: "4px" }}
                      >
                        {jsonStr}
                      </SyntaxHighlighter>
                      {visible.length < filtered.length && (
                        <Box display="flex" justifyContent="center" mt={2}>
                          <Button
                            variant="outlined"
                            onClick={() => setJsonVisibleItems((prev) => prev + 20)}
                          >
                            Load More ({visible.length} / {filtered.length})
                          </Button>
                        </Box>
                      )}
                      {jsonSearchQuery.trim() && (
                        <Typography variant="caption" color="text.secondary" mt={1} display="block">
                          {filtered.length} item{filtered.length !== 1 ? "s" : ""} matching &quot;{jsonSearchQuery}&quot;
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Failed to load {tenantOverview?.dataFeedConnector?.configurations?.dataType?.toUpperCase() || "data"} preview.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {selectedReportId && filteredProcessesList.length > 0 && (
        <OnboardingProcess
          onboardingStatus={onboardingStatus}
          processesList={monitoredProcessesList}
          finishedCounts={finishedCounts}
          reportId={selectedReportId}
          traceId={selectedTraceId}
          onTraceIdClick={handleOpenLogModal}
          apiKey={apiKey}
        />
      )}
      </>
      )}

      {activeSubTab === 1 && (
        <ImporterTestsTab
          apiKey={apiKey}
          tenants={tenants.length > 0 ? tenants : externalTenants}
          selectedTenantId={selectedTenantId}
          setSelectedTenantId={setSelectedTenantId}
          onOpenOverview={handleOpenOverview}
        />
      )}
      </Box>

      <Dialog
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "80vh",
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background:
              "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <CloudSyncOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                Process Logs
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                Inspect trace details, prompts, execution timing, and process diagnostics.
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setLogModalOpen(false)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#fff7f1",
                color: "#f97316",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          <LogAgents initialTraceId={selectedLogTraceId} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImporterStatusTab;
